import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getPageContent } from "../../utils";
import { useAuth } from "../../hooks/useAuth";
import {
    selectMultipleApplicationData,
    selectMultipleApplicationStatus,
    selectMultipleApplicationError,
    initializeAsync,
} from "./multipleApplicationSlice";
import { Box, Button, Card, CardContent, CardHeader, Container, Divider, Typography } from "@mui/material";
import { InlineText } from "../../components/InlineText";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import { useNavigate } from "react-router-dom";
import { PropertyDetailsCard } from "../../components/PropertyDetailsCard";
import { DesktopHeader } from "../../components/DesktopHeader";
import { useMediaQuery } from "react-responsive";
import { ReactComponent as HomeIcon } from "../../assets/svgs/home-fill.svg";
import { get } from "../../api";
import { Modal } from "../../components/Modal";
import { LoadingButton } from "@mui/lab";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { format } from "date-fns";

// Add custom imports here

const MultipleApplication = () => {
    const data = useSelector(selectMultipleApplicationData);
    const status = useSelector(selectMultipleApplicationStatus);
    const error = useSelector(selectMultipleApplicationError);
    const dispatch = useDispatch();
    const { getUserDetailsNotAsync, setUserDetailsNotAsync } = useAuth();
    let userDetails = getUserDetailsNotAsync();
    const navigate = useNavigate();
    const [openNewApplicationModal, setOpenNewApplicationModal] = useState(false);
    const [startHeaderActionLoading, setStartHeaderActionLoading] = useState(false);
    const [startModalActionLoading, setStartModalActionLoading] = useState(false);
    const [propertyOptions, setPropertyOptions] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState("");
    const isDesktop = useMediaQuery({ minWidth: 1180 });

    // Add Variables

    useEffect(() => {
        if (status === "idle") {
            dispatch(initializeAsync());
        }
    }, [status, dispatch]);

    useEffect(() => {
        get("listing/getListingRecordsForPicklist").then((result) => {
            setPropertyOptions(
                result.data.map((item) => {
                    return { label: item.propertyAddress, value: item.salesforceId };
                })
            );
        });
    }, []);

    const getApplications = () => {
        return userDetails.applicants?.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
    };

    const handleContinue = (applicant) => {
        userDetails.applicant = applicant;
        userDetails["listingId"] = applicant.application.applicationListingId;
        setUserDetailsNotAsync(userDetails);

        if (
            userDetails?.applicant?.status === "Submitted" ||
            userDetails?.applicant?.status === "Processing" ||
            userDetails?.applicant?.status === "Cancelled" ||
            userDetails?.applicant?.status === "Declined" ||
            userDetails?.applicant?.status === "Approved"
        ) {
            navigate("/application-completion", {
                replace: true,
            });
        } else {
            navigate("/", { replace: false });
        }
    };

    const createNewApplication = async (listingId) => {
        let result = await get(`applicant/getApplicants`, {
            emailId: encodeURIComponent(userDetails.username),
            listingId: listingId ? encodeURIComponent(listingId) : "",
            convertLead: "yes",
            forceCreate: "yes",
        });

        if (result.isSuccess && result.data && result.data.length > 0) {
            let activeApplication = result.data.find(
                (item) =>
                    item.application.status !== "Approved" &&
                    item.application.status !== "Canceled" &&
                    item.application.status !== "Declined"
            );
            if (activeApplication) {
                userDetails["applicant"] = activeApplication;
            } else {
                userDetails["applicant"] = result.data[0];
            }
            userDetails["applicants"] = result.data;
            setUserDetailsNotAsync(userDetails);
            navigate("/application", { replace: true });
        }
        setStartHeaderActionLoading(false);
        setStartModalActionLoading(false);
        setOpenNewApplicationModal(false);
        setSelectedProperty("");
    };

    const onStartClick = async () => {
        //userDetails["listingId"] = "a0TDp000000hGmvMAE";
        if (userDetails["listingId"]) {
            setStartHeaderActionLoading(true);
            createNewApplication(userDetails["listingId"]);
        } else {
            setOpenNewApplicationModal(true);
        }
    };

    const onStartApplicationYesClick = () => {
        setStartModalActionLoading(true);
        if (selectedProperty) {
            createNewApplication(selectedProperty);
            //createNewApplication("a0TDp000000hGmvMAE");
        } else {
            createNewApplication("");
        }
    };

    const handlePropertyChange = (event, value) => {
        if (value) {
            setSelectedProperty(value.value);
        } else {
            setSelectedProperty("");
        }
    };

    const displayStartButton = () => {
        let approvedApplication = userDetails.applicants.find((item) => item.application.status === "Approved");

        return approvedApplication ? false : true;
    };

    let body = (
        <>
            {status === "initalLoading" ? (
                <></>
            ) : (
                <>
                    <Container>
                        {userDetails.applicant && (
                            <DesktopHeader
                                applicant={userDetails.applicant}
                                isDesktop={isDesktop}
                                action={
                                    <>
                                        {displayStartButton() && (
                                            <Box>
                                                <LoadingButton
                                                    onClick={onStartClick}
                                                    color="primary"
                                                    loading={startHeaderActionLoading}
                                                    fullWidth
                                                    variant="contained"
                                                    sx={{ height: "45px" }}
                                                    id="next-btn">
                                                    Start New Application
                                                </LoadingButton>
                                                <Typography
                                                    variant="h6"
                                                    component="div"
                                                    color="text.secondary"
                                                    sx={{
                                                        textAlign: isDesktop ? "right" : "center",
                                                        marginTop: "4px",
                                                        fontSize: "12px",
                                                    }}>
                                                    Looking to apply for a different property?
                                                </Typography>
                                            </Box>
                                        )}
                                    </>
                                }></DesktopHeader>
                        )}

                        <Grid sx={{ width: "100%", color: "#164C63" }} container spacing={isDesktop ? 3 : 0}>
                            <Grid sx={{ pr: "0", pl: "0px" }} item xs={12}>
                                <Card
                                    sx={{
                                        width: "100%",
                                        p: "0px",
                                        display: "block",
                                        mt: { xs: "16px", lg: "0px" },
                                        minHeight: "calc(100vh - 20rem)",
                                    }}>
                                    <CardHeader
                                        title={
                                            <Box sx={{ fontSize: "20px", fontWeight: "500", p: "8px 1rem" }}>
                                                Your Applications
                                            </Box>
                                        }
                                        sx={{ p: "0px", borderBottom: "1px solid #E1E1E1" }}></CardHeader>
                                    <CardContent sx={{ width: "100%" }}>
                                        <Box
                                            sx={{
                                                m: "1rem",
                                                color: "#164C63",
                                                marginRight: "0px",
                                            }}>
                                            <Grid sx={{ width: "100%", justifyContent: "" }} spacing={3} container>
                                                {getApplications()?.length > 0 && (
                                                    <>
                                                        {getApplications().map((applicant) => (
                                                            <Grid item xs={12} lg={4}>
                                                                <Card sx={{ width: "100%", p: "0px" }}>
                                                                    <CardContent sx={{ width: "100%" }}>
                                                                        <Box
                                                                            sx={{
                                                                                justifyContent: "start",
                                                                                alignItems: "center",
                                                                                display: "flex",
                                                                                padding: ".8rem .8rem 0 .8rem",
                                                                            }}>
                                                                            <Box>
                                                                                <Box
                                                                                    sx={{
                                                                                        p: "8px",
                                                                                        borderRadius: "5px",
                                                                                        border: "solid 1px #ececec",
                                                                                        color: "#02afd4",
                                                                                        height: "50px",
                                                                                        marginRight: "10px",
                                                                                        width: "50px",
                                                                                        display: "flex",
                                                                                        justifyContent: "center",
                                                                                        alignItems: "center",
                                                                                    }}>
                                                                                    <HomeIcon
                                                                                        sx={{
                                                                                            fontSize: "3rem",
                                                                                        }}></HomeIcon>
                                                                                </Box>
                                                                            </Box>

                                                                            <Box>
                                                                                <Typography
                                                                                    variant="cardHeading"
                                                                                    component="div">
                                                                                    {applicant.application?.name}
                                                                                </Typography>
                                                                                <Typography
                                                                                    variant="h6"
                                                                                    component="div"
                                                                                    color="text.secondary">
                                                                                    {applicant.primaryApplicant
                                                                                        ? "As primary applicant"
                                                                                        : "As co-applicant"}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                        <Divider sx={{ my: ".8rem" }}></Divider>
                                                                        <Box sx={{ p: "0 .8rem 0rem .8rem" }}>
                                                                            <Box
                                                                                sx={{
                                                                                    marginTop: "10px",
                                                                                }}>
                                                                                <Typography
                                                                                    variant="cardHeading"
                                                                                    component="div">
                                                                                    Status :{" "}
                                                                                    {applicant.application?.status ===
                                                                                    "Approved" ? (
                                                                                        <span
                                                                                            style={{
                                                                                                color: "#10AE4F",
                                                                                            }}>
                                                                                            Approved
                                                                                        </span>
                                                                                    ) : (
                                                                                        <span
                                                                                            style={{
                                                                                                color: "#991222",
                                                                                            }}>
                                                                                            {
                                                                                                applicant.application
                                                                                                    ?.status
                                                                                            }
                                                                                        </span>
                                                                                    )}
                                                                                </Typography>

                                                                                <Grid container sx={{ mt: "1rem" }}>
                                                                                    <Grid item xs={6}>
                                                                                        <Typography
                                                                                            variant="h6"
                                                                                            component="div"
                                                                                            color="text.secondary">
                                                                                            Created Date
                                                                                        </Typography>
                                                                                        <Typography
                                                                                            sx={{ fontWeight: "500" }}
                                                                                            variant="cardHeading"
                                                                                            component="div">
                                                                                            {format(
                                                                                                new Date(
                                                                                                    applicant?.createdDate
                                                                                                ),
                                                                                                "MMM dd, yyyy"
                                                                                            )}
                                                                                        </Typography>
                                                                                    </Grid>
                                                                                    <Grid item xs={6}>
                                                                                        <Typography
                                                                                            variant="h6"
                                                                                            component="div"
                                                                                            color="text.secondary">
                                                                                            Submitted Date
                                                                                        </Typography>
                                                                                        {applicant?.submittedDate && (
                                                                                            <Typography
                                                                                                sx={{
                                                                                                    fontWeight: "500",
                                                                                                }}
                                                                                                variant="cardHeading"
                                                                                                component="div">
                                                                                                {format(
                                                                                                    new Date(
                                                                                                        applicant.submittedDate
                                                                                                    ),
                                                                                                    "MMM dd, yyyy"
                                                                                                )}
                                                                                            </Typography>
                                                                                        )}
                                                                                    </Grid>
                                                                                </Grid>
                                                                            </Box>
                                                                        </Box>
                                                                        {applicant?.application
                                                                            ?.applicationListingId ? (
                                                                            <>
                                                                                <Divider sx={{ my: ".8rem" }}></Divider>
                                                                                <Box sx={{ p: "0 .8rem .8rem .8rem" }}>
                                                                                    <Box
                                                                                        sx={{
                                                                                            marginTop: "10px",
                                                                                            borderRadius: "5px",
                                                                                            minHeight: "74px",
                                                                                        }}>
                                                                                        <PropertyDetailsCard
                                                                                            item={applicant.application}
                                                                                            dontShowNoProperty={
                                                                                                true
                                                                                            }></PropertyDetailsCard>
                                                                                    </Box>
                                                                                </Box>
                                                                            </>
                                                                        ) : (
                                                                            <Box
                                                                                sx={{ p: "0 .8rem .8rem .8rem" }}></Box>
                                                                        )}
                                                                    </CardContent>
                                                                </Card>
                                                            </Grid>
                                                        ))}
                                                    </>
                                                )}
                                            </Grid>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Container>
                    <Modal
                        open={openNewApplicationModal}
                        title="Start New Application"
                        onClose={() => {
                            setOpenNewApplicationModal(false);
                        }}
                        maxWidth="md"
                        actions={
                            <LoadingButton
                                onClick={onStartApplicationYesClick}
                                color="primary"
                                loading={startModalActionLoading}
                                fullWidth
                                variant="contained"
                                sx={{ height: "45px" }}
                                id="next-btn">
                                Start Application
                            </LoadingButton>
                        }>
                        <Box sx={{ minHeight: isDesktop ? "400px" : "300px" }}>
                            <Autocomplete
                                disablePortal
                                id="property-combobox"
                                fullWidth
                                options={propertyOptions}
                                onChange={handlePropertyChange}
                                renderInput={(params) => <TextField fullWidth {...params} label="Property" />}
                            />
                            <Typography
                                variant="h6"
                                component="div"
                                color="text.secondary"
                                sx={{ textAlign: "left", marginTop: "10px" }}>
                                You can also proceed without selecting a property
                            </Typography>
                        </Box>
                    </Modal>
                </>
            )}
        </>
    );

    return getPageContent(body, status, error);
};

export { MultipleApplication };
